/*!
 * Store selector rewrite
 * Builds country options from section settings and handles localization form
 */
(function () {
  "use strict";

  const config = window.theme?.storeSelectorData;
  const LOG_PREFIX = "[StoreSelector]";
  const debugLog = (...args) => {
    if (window._storeSelectorDebug !== false) {
      console.log(LOG_PREFIX, ...args);
    }
  };

  if (
    !config ||
    !Array.isArray(config.options) ||
    config.options.length === 0
  ) {
    console.warn(
      LOG_PREFIX,
      "Missing store selector configuration. Aborting.",
      config
    );
    return;
  }

  class StoreSelector {
    constructor(configuration) {
      this.config = configuration;
      this.modal = document.querySelector(".store-selector__modal");
      this.select = this.modal?.querySelector(".store-selector__select");
      this.changeButton = this.modal?.querySelector(
        ".store-selector__change-store-button"
      );
      this.form = document.querySelector("[data-store-selector-form]");
      this.triggers = Array.from(
        document.querySelectorAll(".store-selector__trigger")
      );
      this.triggerSelects = Array.from(
        document.querySelectorAll(".store-selector__dropdown-select")
      );
      this.countryInput =
        this.form?.querySelector('input[name="country_code"]') || null;
      this.languageInput =
        this.form?.querySelector('input[name="language_code"]') || null;

      if (
        !this.modal ||
        !this.select ||
        !this.changeButton ||
        !this.form ||
        !this.countryInput
      ) {
        return;
      }

      this.options = this.config.options.map((option) =>
        this.normalizeOption(option)
      );
      this.optionMap = new Map();
      this.optionByCountry = new Map();
      this.options.forEach((option) => {
        this.optionMap.set(option.id, option);
        if (!this.optionByCountry.has(option.countryCode)) {
          this.optionByCountry.set(option.countryCode, option);
        }
      });
      this.fallbackCountryCode = (
        this.config.fallbackCountryCode || "US"
      ).toUpperCase();
      this.fallbackCountryName =
        this.config.fallbackCountryName || "United States";
      this.fallbackDescription =
        this.config.fallbackDescription || "Please select a country";
      this.isDesignMode = Boolean(window.Shopify && Shopify.designMode);
      this.showInDesignMode = Boolean(this.config.showInDesignMode);

      this.availableCountries = new Map(
        (this.config.availableCountries || []).map((country) => [
          (country.iso_code || "").toUpperCase(),
          country,
        ])
      );
      if (this.availableCountries.size === 0) {
        console.warn(
          LOG_PREFIX,
          "No available countries returned from localization; all selections will redirect."
        );
      }
      this.currentCountryCode = (
        this.config.currentCountryCode || ""
      ).toUpperCase();
      this.currentLanguageCode = (
        this.config.currentLanguageCode || ""
      ).toLowerCase();
      this.selectedOption = null;

      this.init();
      debugLog("Initialized selector.", {
        options: this.options,
        availableCountries: Array.from(this.availableCountries.keys()),
        currentCountryCode: this.currentCountryCode,
        currentLanguageCode: this.currentLanguageCode,
      });
    }

    normalizeOption(option) {
      return {
        ...option,
        countryCode: (option.countryCode || "").toUpperCase(),
        languageCode: (option.languageCode || "").toUpperCase(),
        flagIcon: option.flagIcon || null,
      };
    }

    findOption(countryCode, languageCode = "") {
      const upperCountry = (countryCode || "").toUpperCase();
      const upperLang = (languageCode || "").toUpperCase();
      if (!upperCountry) return null;

      if (upperLang) {
        const exactMatch = this.options.find(
          (option) =>
            option.countryCode === upperCountry &&
            option.languageCode === upperLang
        );
        if (exactMatch) return exactMatch;
      }

      return this.optionByCountry.get(upperCountry) || null;
    }

    clearCountryQueryParam() {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has("country")) {
          url.searchParams.delete("country");
          const newPath =
            url.pathname + (url.search ? url.search : "") + url.hash;
          window.history.replaceState({}, "", newPath || url.pathname);
        }
      } catch (error) {
        // ignore
      }
    }

    getDisplayName(option) {
      const name = option.countryName || option.countryCode || "Country";
      const currency = option.currencyCode
        ? ` (${option.currencyCode.toUpperCase()})`
        : "";
      return `${name}${currency}`.trim();
    }

    buildOptionMarkup(option) {
      const label = this.getDisplayName(option);
      const code = option.countryCode || "";
      return `<option value="${this.escapeHtml(
        option.id
      )}" data-country-code="${this.escapeHtml(code)}">${this.escapeHtml(
        label
      )}</option>`;
    }

    buildOptionsMarkup() {
      if (!this.options.length) {
        return '<option selected="selected" disabled>No countries available</option>';
      }

      const popular = [];
      const global = [];

      this.options.forEach((option) => {
        const group =
          (option.optionGroup || "popular") === "popular" ? popular : global;
        group.push(this.buildOptionMarkup(option));
      });

      const parts = [];

      if (popular.length) {
        parts.push("<option disabled>Popular</option>");
        popular.forEach((markup) => parts.push(markup));
      }

      if (popular.length && global.length) {
        parts.push("<option disabled>──────────</option>");
      }

      if (global.length) {
        parts.push("<option disabled>All countries</option>");
        global.forEach((markup) => parts.push(markup));
      }

      return parts.join("");
    }

    populatePrimarySelect() {
      if (!this.select) return;
      this.select.innerHTML = this.buildOptionsMarkup();
    }

    populateTriggerSelects() {
      if (!this.triggerSelects.length) return;
      const markup = this.buildOptionsMarkup();
      this.triggerSelects.forEach((select) => {
        select.innerHTML = markup;
      });
      this.syncSelectValues();
    }

    syncSelectValues({ modal = true, triggers = true } = {}) {
      if (modal && this.select && this.selectedOption) {
        this.select.value = this.selectedOption.id;
      }

      if (triggers && this.triggerSelects.length && this.selectedOption) {
        this.triggerSelects.forEach((select) => {
          select.value = this.selectedOption.id;
        });
      }
    }

    init() {
      this.populatePrimarySelect();
      this.populateTriggerSelects();
      this.attachEvents();

      const savedSelection = this.getSavedSelection();
      let initialOption =
        this.findOption(this.currentCountryCode, this.currentLanguageCode) ||
        savedSelection ||
        this.options[0];

      if (initialOption) {
        this.setSelectedOption(initialOption);
        debugLog("Initial option selected.", initialOption);
      } else {
        console.warn(LOG_PREFIX, "No initial option could be determined.");
      }

      this.handleCountryQueryParam();
      this.detectUserLocation();
      this.initExternalTriggers();
    }

    attachEvents() {
      this.select.addEventListener("change", (event) => {
        const option = this.optionMap.get(event.target.value);
        debugLog("Dropdown changed.", event.target.value, option);
        if (option) {
          // Modal dropdown should not update header trigger until action is taken
          this.setSelectedOption(option, {
            updateTrigger: false,
            syncTriggerSelects: false,
          });
        }
      });

      this.changeButton.addEventListener("click", () => this.handleAction());
      this.setupModalCloseHandlers();
    }

    initExternalTriggers() {
      if (!this.triggerSelects.length) return;
      this.triggerSelects.forEach((dropdown) => {
        dropdown.addEventListener("change", (event) => {
          const value = (event.target.value || "").trim();
          debugLog("External trigger changed.", value);
          if (!value) return;
          const option = this.optionMap.get(value);
          if (option) {
            this.setSelectedOption(option);
            this.handleAction();
          }
        });
      });
    }

    handleAction() {
      if (!this.selectedOption) return;

      debugLog("Handling action.", {
        option: this.selectedOption,
        localizationSupported: this.isLocalizationSupported(
          this.selectedOption
        ),
      });

      if (this.isLocalizationSupported(this.selectedOption)) {
        this.submitLocalizationForm(this.selectedOption);
      } else if (this.selectedOption.redirectUrl) {
        this.redirectToStore(this.selectedOption);
      } else {
        console.warn(
          LOG_PREFIX,
          "Selected option has neither localization support nor redirect URL.",
          this.selectedOption
        );
      }
    }

    isLocalizationSupported(option) {
      return this.availableCountries.has(option.countryCode);
    }

    submitLocalizationForm(option) {
      if (!this.form || !this.countryInput) return;
      debugLog("Submitting localization form.", {
        countryCode: option.countryCode,
      });
      this.countryInput.value = option.countryCode;
      if (this.languageInput) {
        const languageCode = option.languageCode || "";
        if (languageCode) {
          this.languageInput.value = languageCode;
        }
      }

      this.clearCountryQueryParam();

      this.saveSelection();
      this.form.submit();
    }

    redirectToStore(option) {
      let targetUrl = option.redirectUrl;
      if (!targetUrl) return;

      debugLog("Redirecting to external store.", {
        baseUrl: targetUrl,
        languageCode: option.languageCode || null,
        appendCountryParam: option.appendCountryParam,
      });

      try {
        const url = new URL(targetUrl, window.location.origin);
        if (option.appendCountryParam) {
          url.searchParams.set("country", option.countryCode);
        }
        this.saveSelection();
        window.location.href = url.toString();
        return;
      } catch (error) {
        // Fallback to raw string below
      }

      if (option.appendCountryParam) {
        const joiner = targetUrl.includes("?") ? "&" : "?";
        targetUrl = `${targetUrl}${joiner}country=${option.countryCode}`;
      }

      this.saveSelection();
      window.location.href = targetUrl;
    }

    setSelectedOption(option, options = {}) {
      const {
        updateTrigger = true,
        syncModalSelect = true,
        syncTriggerSelects = true,
      } = options;
      this.selectedOption = option;
      debugLog("Selected option updated.", option);
      this.syncSelectValues({
        modal: syncModalSelect,
        triggers: syncTriggerSelects,
      });
      this.updateContent(option);
      if (updateTrigger) {
        this.updateTriggerDisplay(option);
      }
    }

    updateContent(option) {
      const elements = {
        title: this.modal.querySelector(".store-selector__modal__title"),
        description: this.modal.querySelector(
          ".store-selector__description-text"
        ),
        popupText: this.modal.querySelector(".store-selector__popup-text"),
        paymentSwedish: this.modal.querySelector(
          ".store-selector__payment-methods-swedish"
        ),
        paymentInternational: this.modal.querySelector(
          ".store-selector__payment-methods-international"
        ),
      };

      const countryName = option.countryName || option.countryCode;
      const titleCountry = option.countryName || option.countryCode;
      const baseCountryForDefault = (
        option.countryName ||
        option.countryCode ||
        "Country"
      ).split(" - ")[0];

      if (elements.title) {
        if (option.modalTitle) {
          elements.title.innerHTML = option.modalTitle.replace(
            /{COUNTRY}/g,
            titleCountry
          );
        } else {
          elements.title.innerHTML = "Welcome!<br>We ship to {COUNTRY}".replace(
            /{COUNTRY}/g,
            baseCountryForDefault
          );
        }
      }

      if (elements.description) {
        const desc = option.description || "";
        elements.description.innerHTML = desc.replace(/\n/g, "<br>");
      }

      if (this.changeButton) {
        const buttonTemplate =
          option.changeStoreButton || "Continue to {COUNTRY} store";
        this.changeButton.textContent = buttonTemplate.replace(
          /{COUNTRY}/g,
          countryName
        );
      }

      if (elements.popupText) {
        elements.popupText.textContent =
          option.dropDownLabel || "Change country";
      }

      if (elements.paymentSwedish) {
        const isSweden = option.countryCode === "SE";
        elements.paymentSwedish.classList.toggle("hide", !isSweden);
      }

      if (elements.paymentInternational) {
        const isSweden = option.countryCode === "SE";
        elements.paymentInternational.classList.toggle("hide", isSweden);
      }
    }

    buildTriggerLabel(option) {
      const label =
        option.countryName || option.countryCode || "Select country";
      const currency = option.currencyCode
        ? ` (${option.currencyCode.toUpperCase()})`
        : "";
      return `${label}${currency}`.trim();
    }

    updateTriggerDisplay(option) {
      if (!option || !this.triggers.length) return;
      const label = this.buildTriggerLabel(option);
      this.triggers.forEach((trigger) => {
        const labelEl = trigger.querySelector(
          "[data-store-selector-label-text]"
        );
        if (labelEl) {
          labelEl.textContent = label;
        }
        const labelWrapper = trigger.querySelector(
          "[data-store-selector-label]"
        );
        if (labelWrapper) {
          labelWrapper.classList.add("store-selector__text--ready");
        }
        const flagImg = trigger.querySelector("[data-store-selector-flag-img]");
        if (flagImg) {
          if (!flagImg.dataset.defaultSrc) {
            flagImg.dataset.defaultSrc = flagImg.getAttribute("src") || "";
          }
          const nextSrc = option.flagIcon || flagImg.dataset.defaultSrc;
          if (nextSrc) {
            flagImg.src = nextSrc;
          }
          const altText =
            option.countryName || option.countryCode || "Country flag";
          flagImg.alt = `${altText} flag`;
        }
      });
    }

    handleCountryQueryParam() {
      const params = new URLSearchParams(window.location.search);
      const requestedCountry = (params.get("country") || "").toUpperCase();
      if (!requestedCountry) return;

      if (!this.availableCountries.has(requestedCountry)) {
        debugLog(
          "Country query param ignored because it's not available locally.",
          requestedCountry
        );
        return;
      }
      const option = this.optionByCountry.get(requestedCountry);
      if (!option) return;

      const handledCountry = sessionStorage.getItem(
        "store-selector-country-param"
      );
      if (handledCountry === requestedCountry) return;

      debugLog("Country query param detected.", requestedCountry, option);
      sessionStorage.setItem("store-selector-country-param", requestedCountry);
      this.setSelectedOption(option);
      this.submitLocalizationForm(option);
    }

    detectUserLocation() {
      if (this.isDesignMode && !this.showInDesignMode) {
        debugLog("Design mode: popup suppressed by setting.");
        return;
      }
      // If we arrived here via a cross-domain hop from one of our own store domains,
      // skip the IP-based modal once on this landing page to avoid re-prompting.
      const ref = document.referrer;
      if (ref) {
        try {
          const refHost = new URL(ref).host;
          const currentHost = window.location.host;
          const allowedHosts = [
            "sweedbeauty.com",
            "sweedbeauty.eu",
            "sweedbeauty.global",
            "sweedbeauty.se",
          ];
          if (refHost !== currentHost && allowedHosts.includes(refHost)) {
            const landingOption =
              this.findOption(
                this.currentCountryCode,
                this.currentLanguageCode
              ) || this.findOption(this.currentCountryCode);
            if (landingOption) {
              this.setSelectedOption(landingOption, {
                updateTrigger: false,
                syncTriggerSelects: false,
              });
              this.saveSelection();
            }
            sessionStorage.setItem("store-selector-referrer-hop", "1");
            debugLog(
              "Skipping IP modal due to trusted cross-domain referrer.",
              {
                refHost,
                currentHost,
              }
            );
            return;
          }
        } catch (error) {
          // ignore parse errors
        }
      }

      const params = new URLSearchParams(window.location.search);
      const countryFromUrl = (params.get("country") || "").toUpperCase();
      // If a country query param is present, let the query handler/localization
      // flow run without showing the IP-based modal.
      if (countryFromUrl) {
        debugLog(
          "IP detection skipped because country param is present.",
          countryFromUrl
        );
        return;
      }

      const savedSelection = this.getSavedSelection();
      if (savedSelection) {
        debugLog("Saved selection exists; skipping IP-based modal.");
        return;
      }

      fetch("https://ipapi.co/json/", { signal: AbortSignal.timeout(3000) })
        .then((response) => response.json())
        .then((data) => {
          const iso = (data.country_code || "").toUpperCase();
          if (!iso) {
            debugLog("IP detection returned without ISO code.", data);
            return;
          }
          let option = this.optionByCountry.get(iso);
          let usedFallback = false;
          if (!option) {
            const fallbackOption = this.optionByCountry.get(
              this.fallbackCountryCode
            );
            if (fallbackOption) {
              debugLog(
                "Detected country has no matching store option; using fallback.",
                { iso, fallback: this.fallbackCountryCode }
              );
              option = {
                ...fallbackOption,
                modalTitle: "Welcome!",
                description: this.fallbackDescription,
                changeStoreButton: `Go to ${this.fallbackCountryName}`,
              };
              usedFallback = true;
            } else {
              console.warn(
                LOG_PREFIX,
                "Detected country has no matching store option and no fallback found.",
                iso
              );
              return;
            }
          }

          const savedSelection = this.getSavedSelection();
          if (savedSelection && savedSelection.option.countryCode === iso) {
            debugLog(
              "Skipping modal because detected country matches saved selection.",
              iso
            );
            return;
          }
          if (iso === this.currentCountryCode) {
            debugLog(
              "Skipping modal because detected country matches current store.",
              iso
            );
            return;
          }

          const exceptionCountries = ["BA", "BY", "AL", "SM", "XK", "IS"];
          if (exceptionCountries.includes(iso)) {
            const descElement = this.modal.querySelector(
              ".store-selector__description-text"
            );
            if (descElement) {
              descElement.style.display = "none";
            }
          }

          debugLog("IP detection mismatch.", {
            detectedIso: iso,
            option,
            currentCountry: this.currentCountryCode,
            savedOption: savedSelection?.option || null,
          });
          this.setSelectedOption(option, {
            updateTrigger: false,
            syncTriggerSelects: false,
          });
          this.openModal();
        })
        .catch((error) => {
          debugLog("IP detection failed.", error);
        });
    }

    setupModalCloseHandlers() {
      const closeButton = this.modal.querySelector(".store-selector__close");
      if (closeButton) {
        closeButton.addEventListener("click", () => this.closeModal());
      }
      const modalInner = this.modal.querySelector(
        ".store-selector__modal__inner"
      );
      if (modalInner) {
        modalInner.addEventListener("click", (event) =>
          event.stopPropagation()
        );
      }
    }

    openModal() {
      if (this.isDesignMode && !this.showInDesignMode) return;
      this.modal.classList.add("store-selector__modal--open");
      if (this.config.isLandingStoreSelectorEnabled) {
        this.modal.classList.add("store-selector__landing-layer");
      }
    }

    closeModal() {
      this.modal.classList.remove("store-selector__modal--open");
      if (this.config.isLandingStoreSelectorEnabled) {
        this.modal.classList.remove("store-selector__landing-layer");
      }
    }

    getSavedSelection() {
      const savedOptionId = sessionStorage.getItem("savedStoreOptionId");
      if (savedOptionId && this.optionMap.has(savedOptionId)) {
        return this.optionMap.get(savedOptionId);
      }
      return null;
    }

    saveSelection() {
      if (!this.selectedOption) return;
      sessionStorage.setItem("savedStoreOptionId", this.selectedOption.id);
    }

    escapeHtml(value) {
      if (!value) return "";
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }
  }

  new StoreSelector(config);
})();
